import {
  CompanyType,
  DocumentStatus,
  DocumentType,
} from "@lytra/frontend-common/__generated__/graphql";

export const DOCUMENT_TYPES = {
  [DocumentType.Datasheet]: "Datenblatt",
  [DocumentType.InstallationGuide]: "Montageanleitung",
  [DocumentType.MaintenanceManual]: "Wartungshandbuch",
  [DocumentType.ProductCatalog]: "Produktkatalog",
  [DocumentType.QuickStartGuide]: "Quick Start Guide",
  [DocumentType.ServiceManual]: "Servicehandbuch",
  [DocumentType.TroubleshootingGuide]: "Leitfaden zur Fehlerbehebung",
  [DocumentType.UserManual]: "Benutzerhandbuch",
  [DocumentType.Other]: "Sonstige",
};

// Typed as a total Record so a new DocumentStatus in the schema fails the build here
// rather than silently reaching users as a raw SCREAMING_CASE enum value, which is how
// FAILED_INVALID and SKIPPED_TOO_LARGE went unlabelled.
export const DOCUMENT_STATUS: Record<DocumentStatus, string> = {
  [DocumentStatus.Pending]: "In Warteschlange",
  [DocumentStatus.PendingBatch]: "Angehalten",
  // AwaitingUpload (ADR-0022) means "no bytes in GCS yet". The excluded-vs-queued
  // distinction comes from desiredTier, resolved in documentStatusLabel(); this default
  // is the queued case. desiredTier === 0 renders "Nur Metadaten" instead.
  [DocumentStatus.AwaitingUpload]: "Upload in Warteschlange",
  [DocumentStatus.Indexing]: "Wird indexiert",
  [DocumentStatus.Indexed]: "Indexiert",
  [DocumentStatus.NeedsOcr]: "Benötigt OCR",
  [DocumentStatus.Failed]: "Fehler",
  [DocumentStatus.FailedInvalid]: "Ungültige Datei",
  [DocumentStatus.SkippedTooLarge]: "Datei zu groß",
  [DocumentStatus.Deleting]: "Wird gelöscht",
  [DocumentStatus.Deleted]: "Gelöscht",
  // SKIPPED_UPLOAD was renamed to UPLOADED in eb274bf3a without the frontend following:
  // the old member resolved to `undefined`, so this entry keyed nothing and UPLOADED had
  // no label at all — the UI fell through to the raw "UPLOADED" string.
  [DocumentStatus.Uploaded]: "keine Textdatei",
  [DocumentStatus.IndexingExcluded]: "Von Indizierung ausgeschlossen",
  // Content searchable via the raw-source pool (doc itself is an anchor, not chunked).
  [DocumentStatus.RawIndexed]: "Rohdaten indexiert",
  // Attachment of an outbound mail: no download owed.
  [DocumentStatus.Skipped]: "Nicht geladen",
};

/**
 * Human label for a document's lifecycle status, folding in desiredTier for the
 * AWAITING_UPLOAD case (ADR-0022): tier 0 is deliberately excluded ("Nur Metadaten"),
 * tier >= 1 is queued for upload ("Upload in Warteschlange").
 */
export function documentStatusLabel(
  status: DocumentStatus,
  desiredTier?: number | null,
): string {
  if (status === DocumentStatus.AwaitingUpload && (desiredTier ?? 1) === 0) {
    return "Nur Metadaten";
  }
  return DOCUMENT_STATUS[status] ?? status;
}

export const COMPANY_TYPE = {
  [CompanyType.Retailer]: "Händler",
  [CompanyType.Distributor]: "Distributor",
  [CompanyType.EndCustomer]: "Kunde",
  [CompanyType.Other]: "Andere",
};

export const LANGUAGE = {
  de: "Deutsch",
  en: "Englisch",
  "en-US": "Englisch (US)",
  "en-GB": "Englisch (UK)",
  fr: "Französisch",
  it: "Italienisch",
  es: "Spanisch",
  nl: "Niederländisch",
  pt: "Portugiesisch",
  pl: "Polnisch",
  tr: "Türkisch",
  zh: "Chinesisch",
  ru: "Russisch",
  no: "Norwegisch",
  nb: "Norwegisch (Bokmål)",
  sv: "Schwedisch",
  ja: "Japanisch",
  uk: "Ukrainisch",
  da: "Dänisch",
  sl: "Slowenisch",
  cs: "Tschechisch",
  lt: "Litauisch",
  sk: "Slowakisch",
  et: "Estnisch",
  fi: "Finnisch",
  bg: "Bulgarisch",
  ro: "Rumänisch",
  hu: "Ungarisch",
  ko: "Koreanisch",
  lv: "Lettisch",
  el: "Griechisch",
  hr: "Kroatisch",
};
